import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CircleAlert, KeyRound, LayoutDashboard, LogOut, Pencil } from 'lucide-react';
import PageHeader from '../../components/site/PageHeader';
import { Tabs, toast } from '../../ui';
import accountService from '../../services/accountService';
import EmailVerificationBanner from '../../components/EmailVerificationBanner';
import { applyProfileUpdate, logoutAsync } from '../auth/state/Actions';
import { useAppDispatch } from '../../state/AppStateProvider';
import { USER_GITHUB_PROFILE, USER_NAME } from '../../configs/configs';
import { decodeJWTRole } from '../../utils/securityMiddleware';
import { formatDate } from '../../utils/format';
import OrderHistory from './orderHistory';
import AccountSettingsModal from './components/AccountSettingsModal';
import './style.css';

const readStorage = key => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

// Avatar saved at sign-in (GitHub profile), if it belongs to the current username.
const storedAvatar = username => {
  try {
    const profile = JSON.parse(readStorage(USER_GITHUB_PROFILE) || 'null');
    return profile?.avatarUrl && profile.username?.toLowerCase() === String(username || '').toLowerCase()
      ? profile.avatarUrl
      : null;
  } catch {
    return null;
  }
};

const initials = name =>
  String(name || '')
    .trim()
    .split(/\s+/)
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';

const Avatar = ({ name, username }) => {
  const [failed, setFailed] = useState(false);
  const src = storedAvatar(username);
  if (src && !failed) {
    return <img className="pk-profile__avatar" src={src} alt="" onError={() => setFailed(true)} />;
  }
  return (
    <span className="pk-profile__avatar" aria-hidden="true">
      {initials(name)}
    </span>
  );
};

const ProfileSummary = ({ state, onRetry }) => {
  if (state.status === 'loading') {
    return (
      <section className="pk-card pk-profile" aria-busy="true" aria-label="Loading your profile">
        <span className="pk-skel pk-profile__avatar" />
        <div className="pk-profile__info">
          <span className="pk-skel" style={{ width: 180, height: 22 }} />
          <span className="pk-skel" style={{ width: 240, height: 14 }} />
        </div>
      </section>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="pk-alert" data-tone="danger" role="alert">
        <CircleAlert aria-hidden="true" />
        <div className="pk-order__alert-body">
          <span>{state.error}</span>
          <button type="button" className="pk-btn pk-btn--secondary pk-btn--xs" onClick={onRetry}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { user } = state;
  return (
    <section className="pk-card pk-profile" aria-label="Your profile">
      <Avatar name={user.fullName} username={user.gitHubUsername} />
      <div className="pk-profile__info">
        <p className="pk-profile__name">
          {user.fullName || 'Your account'}
          {String(user.role).toLowerCase() === 'admin' && (
            <span className="pk-badge" data-tone="info">
              Admin
            </span>
          )}
        </p>
        <p className="pk-profile__meta">
          {[user.phone, user.email, user.gitHubUsername && `@${user.gitHubUsername}`].filter(Boolean).join(' · ')}
        </p>
      </div>
    </section>
  );
};

const AccountDetails = ({ user, onEdit, onPassword, onSignOut }) => (
  <div className="pk-stack">
    <section className="pk-card" aria-labelledby="account-details-title">
      <div className="pk-card__head">
        <h2 className="pk-card__title" id="account-details-title">
          Details
        </h2>
        <button type="button" className="pk-btn pk-btn--secondary pk-btn--xs" onClick={onEdit}>
          <Pencil aria-hidden="true" /> Edit
        </button>
      </div>
      <dl className="pk-rows">
        <div>
          <dt>Name</dt>
          <dd>{user?.fullName || '—'}</dd>
        </div>
        <div>
          <dt>Phone</dt>
          <dd>{user?.phone || '—'}</dd>
        </div>
        <div>
          <dt>Email</dt>
          <dd>{user?.email || <span className="pk-muted">Not added</span>}</dd>
        </div>
        <div>
          <dt>GitHub username</dt>
          <dd>
            {user?.gitHubUsername ? (
              <a
                className="pk-link"
                href={`https://github.com/${encodeURIComponent(user.gitHubUsername)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                @{user.gitHubUsername}
              </a>
            ) : (
              <span className="pk-muted">Not added</span>
            )}
          </dd>
        </div>
        {user?.createdDate && (
          <div>
            <dt>Member since</dt>
            <dd>{formatDate(user.createdDate)}</dd>
          </div>
        )}
      </dl>
      {user && !user.gitHubUsername && (
        <p className="pk-hint pk-profile__note">
          Add your GitHub username to receive access to the repositories you buy.
        </p>
      )}
    </section>

    <section className="pk-card" aria-labelledby="account-security-title">
      <div className="pk-card__head">
        <h2 className="pk-card__title" id="account-security-title">
          Password and sign out
        </h2>
      </div>
      <div className="pk-cluster">
        <button type="button" className="pk-btn pk-btn--secondary pk-btn--sm" onClick={onPassword}>
          <KeyRound aria-hidden="true" /> Change password
        </button>
        <button type="button" className="pk-btn pk-btn--danger pk-btn--sm" onClick={onSignOut}>
          <LogOut aria-hidden="true" /> Sign out
        </button>
      </div>
    </section>
  </div>
);

const UserProfile = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'account' ? 'account' : 'orders';

  const [profile, setProfile] = useState({ status: 'loading', user: null, error: '' });
  const [settings, setSettings] = useState({ open: false, tab: 'details' });
  const isAdmin = decodeJWTRole() === 'admin';

  const loadProfile = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setProfile(p => ({ ...p, status: p.user ? p.status : 'loading', error: '' }));
    const result = await accountService.getMe();
    if (result.success && result.data) {
      setProfile({ status: 'ready', user: result.data, error: '' });
      return;
    }
    // The axios client already handles an expired session (401).
    setProfile(p =>
      p.user && quiet
        ? p
        : { status: 'error', user: null, error: result.error || "We couldn't load your profile." }
    );
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const changeTab = key => {
    setSearchParams(key === 'account' ? { tab: 'account' } : {}, { replace: true });
  };

  const openSettings = which => setSettings({ open: true, tab: which });

  const onSaved = user => {
    applyProfileUpdate(dispatch, user);
    if (user) setProfile({ status: 'ready', user, error: '' });
    loadProfile({ quiet: true });
  };

  const signOut = () => {
    logoutAsync(dispatch);
    toast.success('You have signed out.');
    navigate('/', { replace: true });
  };

  const fallbackName = readStorage(USER_NAME) || '';
  const greetingName = (profile.user?.fullName || fallbackName).split(' ')[0];

  return (
    <main className="pk-page pk-account">
      <div className="pk-container">
        <PageHeader
          title={greetingName ? `Hi, ${greetingName}` : 'Your account'}
          subtitle="Your orders, downloads and account details."
          crumbs={[{ label: 'Home', to: '/' }, { label: 'Account' }]}
          aside={
            isAdmin ? (
              <Link to="/admin/dashboard" className="pk-btn pk-btn--secondary pk-btn--sm">
                <LayoutDashboard aria-hidden="true" /> Admin panel
              </Link>
            ) : null
          }
        />

        <div className="pk-stack pk-account__body">
          <ProfileSummary state={profile} onRetry={() => loadProfile()} />

          {profile.status === 'ready' && (
            <EmailVerificationBanner user={profile.user} onChangeEmail={() => openSettings('details')} />
          )}

          <Tabs
            className="pk-account__tabs"
            activeKey={tab}
            onChange={changeTab}
            items={[
              { key: 'orders', label: 'Orders', children: <OrderHistory /> },
              {
                key: 'account',
                label: 'Account',
                children: (
                  <AccountDetails
                    user={profile.user}
                    onEdit={() => openSettings('details')}
                    onPassword={() => openSettings('password')}
                    onSignOut={signOut}
                  />
                ),
              },
            ]}
          />
        </div>
      </div>

      <AccountSettingsModal
        open={settings.open}
        initialTab={settings.tab}
        onClose={() => setSettings(s => ({ ...s, open: false }))}
        onSaved={onSaved}
      />
    </main>
  );
};

export default UserProfile;
